import { STEP_STATUS, STEP_TYPE } from "fieldpro-tools";
import _ from "lodash";

import {
  IActivity,
  IWorkflow,
  IWorkflowReportWithStepOverview,
} from "model/entities/Workflow";
import { resolveCondition } from "utils/expressions/conditionSolver";

export const getNextStepId = (
  selectedWorkflowReport: IWorkflowReportWithStepOverview | undefined,
  selectedWorkflow: IWorkflow,
  onlyActivity = true,
): string | undefined => {
  // first case: we are starting a workflow report. The first step should be selected.
  if (!selectedWorkflowReport) {
    return selectedWorkflow?.starting_step_id;
  }
  // second case: we are continuing an ongoing workflow report. The next step should be selected.
  const nextStepToExecute = getNextStepsToExecute(
    selectedWorkflow,
    selectedWorkflowReport,
    onlyActivity,
  )?.[0];
  return nextStepToExecute;
};

/**
 * Get the next Activity step in the workflow that is not in DONE status
 */
export const getNextStepActivity = (
  selectedWorkflowReport: IWorkflowReportWithStepOverview | undefined,
  selectedWorkflow: IWorkflow,
  activities: IActivity[],
) => {
  const stepId = getNextStepId(selectedWorkflowReport, selectedWorkflow);
  if (!stepId) {
    return {
      stepId: undefined, // WARN: used to be stepId, but that is not correct ?
      activity: undefined,
    };
  }

  const step = _.find(selectedWorkflow.steps, (step) => step.id === stepId);
  const activity = _.find(
    activities,
    (activity) => activity.id === step?.schema_id,
  );

  if (!activity) {
    return {
      stepId: undefined,
      activity: undefined,
    };
  }

  return {
    stepId,
    activity,
  };
};

export const getNextStepsToExecute = (
  workflow: IWorkflow,
  workflowReport: IWorkflowReportWithStepOverview,
  onlyActivity = true,
) => {
  if (!workflowReport?.step_overview) {
    return [];
  }

  const allSteps = onlyActivity
    ? workflow.steps.filter((s) => s.type === STEP_TYPE.ACTIVITY)
    : workflow.steps;

  const stepsDoneIds = allSteps
    .filter(
      (s) =>
        workflowReport.step_overview.find((so) => so.step_id === s.id)
          ?.step_status === STEP_STATUS.DONE,
    )
    .map((s) => s.id);

  const result: string[] = [];
  for (const chaining of workflow.chainings) {
    // iterate on all the chaining in the workflow
    const isOriginStepEditable = allSteps.find(
      (s) => s.id === chaining.origin_step,
    )?.is_editable;

    if (
      stepsDoneIds.includes(chaining.destination_step) &&
      !isOriginStepEditable
    ) {
      // keep only steps not done yet or steps that are editable
      continue;
    }

    if (!stepsDoneIds.includes(chaining.origin_step)) {
      // keep only steps with the previous step done
      continue;
    }

    if (!allSteps.map((s) => s.id).includes(chaining.destination_step)) {
      // keep only steps that are activities
      continue;
    }

    if (
      !_.isEmpty(chaining?.conditions) &&
      !resolveCondition(chaining.conditions)
    ) {
      // keep only the chainings with a condition equal to true
      continue;
    }

    result.push(chaining.destination_step);
  }
  return result;
};
